import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';
import logo from '../assets/logo.svg';
import { useTranslation } from 'react-i18next';
import './Navbar.css';

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);
  const scrollLockY = useRef(0);
  const skipScrollRestore = useRef(false);
  const [t, i18n] = useTranslation('global');
  const location = useLocation();
  const currentLang = i18n.language || 'sr';

  const closeMenu = ({ navigating = false } = {}) => {
    if (navigating) {
      skipScrollRestore.current = true;
    }
    setMenuOpen(false);
    setProductsOpen(false);
  };

  const changeLang = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    closeMenu({ navigating: true });
  }, [location.pathname]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      // Desktop only: close products dropdown when clicking outside the header nav
      if (window.innerWidth <= 980) return;
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setProductsOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  useEffect(() => {
    if (!menuOpen) {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      return undefined;
    }

    scrollLockY.current = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollLockY.current}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (skipScrollRestore.current) {
        skipScrollRestore.current = false;
        return;
      }
      window.scrollTo(0, scrollLockY.current);
    };
  }, [menuOpen]);

  const renderMenuItems = () => (
    <>
      <li>
        <Link to="/#home" onClick={() => closeMenu({ navigating: true })}>
          {t('navbar.home')}
        </Link>
      </li>
      <li>
        <a href="/#about" onClick={() => closeMenu()}>
          {t('navbar.about')}
        </a>
      </li>

      <li
        className={`dropdown ${productsOpen ? 'open' : ''}`}
        onMouseEnter={() => window.innerWidth > 980 && setProductsOpen(true)}
        onMouseLeave={() => window.innerWidth > 980 && setProductsOpen(false)}
      >
        <button
          type="button"
          className="dropdown-toggle"
          onClick={() => setProductsOpen((v) => !v)}
          aria-expanded={productsOpen}
        >
          {t('navbar.products')}
          <FaChevronDown className="chevron" aria-hidden="true" />
        </button>
        <ul className="dropdown-menu">
          <li>
            <Link to="/idroflog" onClick={() => closeMenu({ navigating: true })}>
              Idroflog
            </Link>
          </li>
          <li>
            <Link to="/laluvit" onClick={() => closeMenu({ navigating: true })}>
              Ialuvit
            </Link>
          </li>
          <li>
            <Link
              to="/oftaial-plus"
              onClick={() => closeMenu({ navigating: true })}
            >
              Oftaial Plus
            </Link>
          </li>
        </ul>
      </li>

      <li>
        <a href="/#our_partners" onClick={() => closeMenu()}>
          {t('navbar.our_partners')}
        </a>
      </li>
      <li>
        <Link to="/vesti" onClick={() => closeMenu({ navigating: true })}>
          {t('navbar.news')}
        </Link>
      </li>
      <li>
        <a
          href="/#contact"
          onClick={() => closeMenu()}
          className="nav-cta"
        >
          {t('navbar.contact')}
        </a>
      </li>
    </>
  );

  const mobileOverlay =
    typeof document !== 'undefined'
      ? createPortal(
          <>
            <ul
              id="primary-menu-mobile"
              className={`nav-menu nav-menu--drawer ${menuOpen ? 'active' : ''}`}
              aria-hidden={!menuOpen}
            >
              {renderMenuItems()}
            </ul>
            {menuOpen && (
              <button
                type="button"
                className="nav-backdrop"
                aria-label="Close menu"
                onClick={() => closeMenu()}
              />
            )}
          </>,
          document.body
        )
      : null;

  return (
    <header className={`site-header ${scrolled ? 'is-scrolled' : ''}`}>
      <nav className="navbar" ref={menuRef} aria-label="Primary">
        <div className="navbar-container bm-container">
          <Link to="/" className="navbar-logo" onClick={() => closeMenu({ navigating: true })}>
            <img src={logo} alt="BeoMedica" width="220" height="96" />
          </Link>

          <div className="navbar-tools">
            <div className="lang-switch" role="group" aria-label="Language">
              <button
                type="button"
                className={currentLang === 'sr' ? 'active' : ''}
                onClick={() => changeLang('sr')}
                aria-pressed={currentLang === 'sr'}
              >
                SR
              </button>
              <button
                type="button"
                className={currentLang === 'en' ? 'active' : ''}
                onClick={() => changeLang('en')}
                aria-pressed={currentLang === 'en'}
              >
                EN
              </button>
            </div>

            <button
              className="menu-toggle"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="primary-menu-mobile"
              type="button"
            >
              {menuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>

          <ul id="primary-menu" className="nav-menu nav-menu--desktop">
            {renderMenuItems()}
          </ul>
        </div>
      </nav>
      {mobileOverlay}
    </header>
  );
};

export default Navbar;
